import {Injectable} from '@angular/core';
import {ApiAuthenticateResponseModel} from '../model/api-authenticate-response.model';

/**
 * @class
 * @description This class provides methods to interact with the session storage.
 * @export
 * @name SessionStorageService
 * @service
 */
@Injectable({

  // We'll want this service provided as a singleton.
  providedIn: 'root'
})
export class SessionStorageService {

  /**
   * @constructor
   * @description This method creates an instance of the StorageService.
   * @memberof SessionStorageService
   * @public
   */
  public constructor() {
  }

  /**
   * @description This method normalizes a session storage key.
   * @memberof SessionStorageService
   * @method
   * @name SessionStorageService#normalizeSessionKey
   * @param {string} sessionKey The unique identifying name of the session to retrieve.
   * @private
   */
  private normalizeSessionKey(sessionKey: string) {

    // We're done, convert the session key to lower-case and trim it then return it.
    return sessionKey.toLowerCase().trim();
  }

  /**
   * @description This method clears the session storage for the specified session key.
   * @memberof SessionStorageService
   * @method
   * @name SessionStorageService#delete
   * @param {string} sessionKey The unique identifying name of the session to retrieve.
   * @public
   * @returns {void}
   * @template TSession The type of the user session we're working with.
   */
  public delete<TSession>(sessionKey: string): void;

  /**
   * @description This method clears the session storage for the specified session key.
   * @memberof SessionStorageService
   * @method
   * @name SessionStorageService#clear
   * @param {string} sessionKey The unique identifying name of the session to retrieve.
   * @public
   * @returns {void}
   */
  public delete(sessionKey: string): void {

    // Remove any existing user from the session storage.
    window.sessionStorage.removeItem(sessionKey);
  }

  /**
   * @description This method checks if the session has expired.
   * @memberof SessionStorageService
   * @method
   * @name SessionStorageService#expired
   * @param {string} sessionKey The unique identifying name of the session to retrieve.
   * @returns {boolean} True if the session has expired, otherwise false.
   * @template TSession The type of the user session we're working with.
   */
  public expired<TSession extends ApiAuthenticateResponseModel>(sessionKey: string): boolean {

    // Localize the session JSON from the session storage.
    const session: TSession | undefined = this.load(sessionKey);

    // Ensure a session actually exists, if not then automatically expire the session.
    if (!session) return true;

    // Localize the current timestamp.
    const now: Date = new Date();

    // Define our boundary UTC timestamp.
    const boundary: Date = new Date(now.getTime() + (now.getTimezoneOffset() * 60000));

    // We're done, return whether the session has expired or not.
    return boundary.getTime() > Date.parse(session.expires);
  }

  /**
   * @description This method returns the number of milliseconds until the session expires.
   * @memberof SessionStorageService
   * @method
   * @name SessionStorageService#expiresIn
   * @param {string} sessionKey The unique identifying name of the session to retrieve.
   * @public
   * @returns {number} The number of milliseconds until the session expires.
   */
  public expiresIn<TSession extends ApiAuthenticateResponseModel>(sessionKey: string): number {

      // Localize the session JSON from the session storage.
      const session: TSession | undefined = this.load(sessionKey);

      // Ensure a session actually exists, if not then automatically expire the session.
      if (!session) return 0;

      // Localize the current timestamp.
      const now: Date = new Date();

      // Define our boundary UTC timestamp.
      const boundary: Date = new Date(now.getTime() + (now.getTimezoneOffset() * 60000));

      // We're done, return whether the session has expired or not.
      return Date.parse(session.expires) - boundary.getTime();
    }

  /**
   * @description This method loads the user session from the session storage.
   * @memberof SessionStorageService
   * @method
   * @name SessionStorageService#load
   * @param {string} sessionKey The unique identifying name of the session to retrieve.
   * @returns {TSession | undefined} The user session or undefined if it doesn't exist.
   * @template TSession The type of the user session we're working with.
   */
  public load<TSession>(sessionKey: string): TSession | undefined {

    // Localize the session JSON from the session storage.
    const sessionJson: string | null = window.sessionStorage.getItem(this.normalizeSessionKey(sessionKey));

    // Ensure we have a JSON representation of the session.
    if (!sessionJson) return undefined;

    // Try to parse the JSON into a session object.
    try {

      // We're done, parse the session JSON into a session object then return it.
      return JSON.parse(sessionJson);
    } catch (_) {

      // Return undefined.
      return undefined;
    }
  }

  /**
   * @description This method checks if the user is online.
   * @memberof SessionStorageService
   * @method
   * @name SessionStorageService#online
   * @param {string} sessionKey The unique identifying name of the session to retrieve.
   * @returns {boolean} True if the user is online, otherwise false.
   * @template TSession The type of the user session we're working with.
   */
  public online<TSession>(sessionKey: string): boolean;

  /**
   * @description This method checks if the user is online.
   * @memberof SessionStorageService
   * @method
   * @name SessionStorageService#online
   * @param {string} sessionKey The unique identifying name of the session to retrieve.
   * @returns {boolean} True if the user is online, otherwise false.
   */
  public online(sessionKey: string): boolean {

    // Check for a user session then return the result.
    return !!window.sessionStorage.getItem(this.normalizeSessionKey(sessionKey));
  }

  /**
   * @description This method saves the user session to the session storage.
   * @memberof SessionStorageService
   * @method
   * @name SessionStorageService#save
   * @param {string} sessionKey The unique identifying name of the session to retrieve.
   * @param {TSession} session The user session to store.
   * @returns {void}
   * @template TSession The type of the user session we're working with.
   */
  public save<TSession>(sessionKey: string, session: TSession): void {

    // Normalize the session key.
    sessionKey = this.normalizeSessionKey(sessionKey);

    // Remove any existing user from the session storage.
    this.delete(sessionKey);

    // Save the user to the session storage.
    window.sessionStorage.setItem(sessionKey, JSON.stringify(session));
  }
}
